import { routePath } from "config/routeMgt/routePath";
import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import homeIcon from "assets/icons/dashboard.png";
import priceIcon from "assets/icons/pricing.png";
import jobsIcon from "assets/icons/jobs.png";
import talentsIcon from "assets/icons/talents.png";
import chatIcon from "assets/icons/chat.png";
import settingsIcon from "assets/icons/settings.png";
import logoutIcon from "assets/icons/logout.png";
import { useSignOut } from "react-auth-kit";
import { useGetRecruiterCount } from "features/home/hooks/useGetRecruiterCount";
import { useGetUserInfo } from "hooks/useGetUserInfo";
import { MessageOutlined } from "@ant-design/icons";
import helpIcon from "assets/icons/support.png";
import { SupportChat } from "features/support/pages/SupportChat";
import ProfileCompletionCard from "./ProfileCompletionCard";
import WarningModal from "../components/WarningModal";
import { useLocation } from "react-router-dom";
import { useCheckPermission } from "./hooks/useHasPermission";
import { refreshUserPermissions } from "utils/refreshUserPermissions";
import { useGetInvitesByStatus } from "../../../features/home/hooks/useGetInvitesByStatus";
import { useGetUnreadMessageCount } from "../../../features/message/hooks/useGetUnreadMessageCount";

interface RecruiterSidebarProps {
  startTour: () => void;
}

export const RecruiterSidebar = ({ startTour }: RecruiterSidebarProps) => {
  const signOut = useSignOut();
  const location = useLocation();
  const [isLogoutModalVisible, setLogoutModalVisible] = useState(false);
  const { data } = useGetRecruiterCount();
  const { data: pendingInvitesData } = useGetInvitesByStatus("pending");
  const { userInfo, token } = useGetUserInfo();
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(location.pathname.includes("smart"));
  const navigate = useNavigate();

  const canViewJobs = useCheckPermission("job.view");
  const canViewInvites = useCheckPermission("invite.view");
  const canViewTalents = useCheckPermission("talent.view");
  const canViewFolders = useCheckPermission("folder.view");
  const canViewMessages = useCheckPermission("message.view");
  const canAccessSmartScreen = useCheckPermission("smartscreen.access");
  const { data: unreadMessageCount = 0 } =
    useGetUnreadMessageCount(canViewMessages);
  const canViewPricing = !(
    userInfo?.recruiter?.parent_id != null ||
    userInfo?.current_team !== undefined
  );

  const canViewSettings = true;

  const handleLogout = () => {
    signOut();
    setLogoutModalVisible(false);
  };

  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      (window as any).refreshSidebarPermissions = async () => {
        console.log("🔄 Refreshing permissions...");
        const result = await refreshUserPermissions(token);
        if (result) {
          console.log("✅ Permissions refreshed, reloading...");
          window.location.reload();
        } else {
          console.error("❌ Failed to refresh permissions");
        }
      };
      console.log(
        "💡 Tip: Run 'refreshSidebarPermissions()' in console to refresh permissions",
      );
    }
  }, [token]);

  return (
    <div className="flex flex-col justify-between h-screen">
      <div className="flex flex-col mt-3 gap-y-3 justify-center px-5">
        <NavLink to={routePath.home} className="sidebarLink">
          <img src={homeIcon} alt="Home Icon" className="h-6" />
          <span className="text-sm">Dashboard</span>
        </NavLink>

        {canViewJobs && (
          <NavLink to={routePath.jobs} className="sidebarLink">
            <img src={jobsIcon} alt="Job Icon" className="h-6" />
            <span className="text-sm ">Jobs</span>
          </NavLink>
        )}

        {canViewInvites && (
          <NavLink
            to={routePath.recruierInvites}
            className="sidebarLink relative"
          >
            <i className="ri-checkbox-line text-xl"></i>
            <span className="text-sm flex items-center gap-6">
              Recruiter Invites
              {pendingInvitesData && pendingInvitesData.length > 0 && (
                <span className="bg-red-500 text-white text-xs font-bold rounded-full px-1.5 py-0.5 min-w-[18px] h-[18px] flex items-center justify-center">
                  {pendingInvitesData.length}
                </span>
              )}
            </span>
          </NavLink>
        )}

        {canViewTalents && (
          <NavLink to={routePath.talents} className="sidebarLink">
            <img src={talentsIcon} alt="folder Icon" className="h-6" />
            <span className="text-sm">Talents</span>
          </NavLink>
        )}

        {canViewFolders && (
          <NavLink to={routePath.folders} className="sidebarLink">
            <i className="ri-folder-2-line text-xl"></i>
            <span className="text-sm">Folders</span>
          </NavLink>
        )}

        {canViewPricing && (
          <NavLink to={routePath.pricing} className="sidebarLink">
            <img src={priceIcon} alt="pricing Icon" className="h-6" />
            <span className="text-sm">Pricing</span>
          </NavLink>
        )}

        {canViewMessages && (
          <NavLink to={routePath.messages} className="sidebarLink relative">
            <img src={jobsIcon} alt="message Icon" className="h-6" />
            <span className="text-sm flex items-center gap-6">
              Messages
              {unreadMessageCount > 0 && (
                <span className="bg-red-500 text-white text-xs font-bold rounded-full px-1.5 py-0.5 min-w-[18px] h-[18px] flex items-center justify-center">
                  {unreadMessageCount > 99 ? "99+" : unreadMessageCount}
                </span>
              )}
            </span>
          </NavLink>
        )}

        {canAccessSmartScreen && (
          <div
            className="sidebarLink flex items-center cursor-pointer"
            onClick={() => setOpenMenu(!openMenu)}
          >
            <img src={chatIcon} alt="chat Icon" className="h-6" />
            <span className="text-sm flex-1">CV SmartScreen</span>

            {/* Arrow indicator */}
            <i
              className={`ri-arrow-down-s-line transition-transform ${
                openMenu ? "rotate-180" : ""
              }`}
            ></i>
          </div>
        )}

        {openMenu && (
          <div className="ml-8 mt-2 space-y-2">
            <NavLink to={routePath.cvchat} className="sidebarLink">
              <span className="text-sm">New Upload</span>
            </NavLink>
            <NavLink to={routePath.cvchatHistory} className="sidebarLink">
              <span className="text-sm">Upload History</span>
            </NavLink>
          </div>
        )}

        {canViewSettings && (
          <NavLink to={routePath.settings} className="sidebarLink">
            <img src={settingsIcon} alt="settings Icon" className="h-6" />
            <span className="text-sm">Settings</span>
          </NavLink>
        )}

        {/* Always show logout */}
        {/* <div className="sidebarLink cursor-pointer" onClick={showLogoutModal}>
          <img src={logoutIcon} alt="logout Icon" className="h-6" />
          <span className="text-sm">Sign Out</span>
        </div> */}

        <div className="flex flex-col mt-3 gap-y-3 justify-center px-5">
          <div className="sidebarLink cursor-pointer" onClick={startTour}>
            <img src={helpIcon} alt="Help Icon" className="h-6" />
            <span className="text-sm">Tour Guide</span>
          </div>
        </div>
      </div>

      <div
        className="fixed bottom-3 right-28 w-14 h-14 bg-primary rounded-full flex items-center justify-center shadow-lg cursor-pointer z-50"
        onClick={() => setIsChatOpen(true)}
      >
        <MessageOutlined className="text-white text-xl" />
      </div>

      {isChatOpen && (
        <SupportChat isOpen={isChatOpen} setIsOpen={setIsChatOpen} />
      )}
      {data?.profile.total !== 100 && (
        <div className="mx-2 mb-[80px]">
          <ProfileCompletionCard
            photo={userInfo?.photo}
            total={data?.profile.total}
            onClick={() => navigate("/settings")}
          />
        </div>
      )}
      <WarningModal
        open={isLogoutModalVisible}
        title="Sign out"
        content={
          <>
            Are you sure you would like to sign <br /> out of your Arno account?
          </>
        }
        onConfirm={handleLogout}
        onCancel={() => setLogoutModalVisible(false)}
        confirmText="Yes, Sign Out"
        cancelText="No"
      />
    </div>
  );
};
